import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { ArrowRight } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const News = () => {
    const [posts, setPosts] = useState([]);

    useSEO({
        title: 'Industrial Cleaning News & Insights — Dry Ice Bulls',
        description: 'News, insights, and updates from Dry Ice Bulls on dry ice blasting, laser cleaning, sandblasting, pressure washing, and industrial cleaning best practices.',
    });

    useEffect(() => {
        api.get('/news').then((r) => setPosts(r.data)).catch(() => {});
    }, []);

    const featured = posts[0];
    const rest = posts.slice(1);

    return (
        <div data-testid="news-page">
            <section className="border-b border-white/10">
                <div className="container-page pt-32 pb-16">
                    <div className="label-caps text-brand-primary mb-6">/ News & insights</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl">
                        The field report
                    </h1>
                    <p className="mt-6 text-white/60 max-w-2xl">
                        Stories, guides and industry insights from 27+ years of industrial cleaning work.
                    </p>
                </div>
            </section>

            <section className="container-page py-16">
                {posts.length === 0 ? (
                    <div className="text-white/50 label-caps" data-testid="news-empty">No articles yet.</div>
                ) : (
                    <>
                        {featured && (
                            <Link
                                to={`/news/${featured.slug || featured.id}`}
                                className="group grid grid-cols-1 lg:grid-cols-2 gap-8 border border-white/10 hover:border-brand-primary transition-colors mb-12"
                                data-testid={`news-featured-${featured.id}`}
                            >
                                <div className="aspect-[4/3] lg:aspect-auto bg-brand-surface overflow-hidden">
                                    {featured.cover_image ? (
                                        <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    ) : null}
                                </div>
                                <div className="p-8 lg:p-12 flex flex-col justify-center">
                                    <div className="label-caps text-brand-primary mb-3">Featured</div>
                                    <div className="label-caps text-white/40 mb-2">
                                        {new Date(featured.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </div>
                                    <h2 className="font-display font-black uppercase tracking-tighter text-3xl md:text-5xl text-white group-hover:text-brand-primary transition-colors">
                                        {featured.title}
                                    </h2>
                                    <p className="text-white/70 mt-4 line-clamp-3">{featured.excerpt}</p>
                                    <span className="mt-6 inline-flex items-center gap-2 label-caps text-brand-primary">
                                        Read article <ArrowRight className="h-4 w-4" />
                                    </span>
                                </div>
                            </Link>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {rest.map((p) => (
                                <Link
                                    key={p.id}
                                    to={`/news/${p.slug || p.id}`}
                                    className="group border border-white/10 bg-brand-surface hover:border-brand-primary transition-colors flex flex-col"
                                    data-testid={`news-card-${p.id}`}
                                >
                                    <div className="aspect-video bg-brand-bg overflow-hidden">
                                        {p.cover_image ? (
                                            <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        ) : null}
                                    </div>
                                    <div className="p-6 flex-1 flex flex-col">
                                        <div className="label-caps text-white/40 mb-2">
                                            {new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </div>
                                        <h3 className="font-display font-black text-xl uppercase tracking-tight text-white group-hover:text-brand-primary transition-colors">{p.title}</h3>
                                        <p className="text-white/60 text-sm mt-2 line-clamp-3">{p.excerpt}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};

export default News;
